import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import PropTypes from 'prop-types'
import { Modal, Form, Select, Radio, Input } from 'antd'
import _ from 'lodash'
import { RegionType, GetRegionType } from './RegionType'

import { getFields } from 'admin/lib/Records/fields'

/** @returns {Promise<false | object>} */
export const showRegionEditModal = async (moduleName, region) => {
  const ref = React.createRef()

  region.regionType = GetRegionType(
    region.regionType,
    region.field,
    region.value
  )
  region.previousField = region.field
  region.previousAnchor = region.value

  return await new Promise(resolve => {
    Modal.confirm({
      icon: null,
      onCancel: () => resolve(false),
      onOk: () => ref.current.submit(),
      content: (
        <FormContent
          moduleName={moduleName}
          region={region}
          onSave={resolve}
          ref={ref}
        />
      )
    })
  })
}

const FormContent = React.forwardRef(({ moduleName, region, onSave }, ref) => {
  const [form] = Form.useForm()
  const firstFieldRef = useRef()

  const [regionType, setRegionType] = useState(region.regionType)

  useEffect(() => {
    setTimeout(() => firstFieldRef.current.focus(), 300)
  }, [])

  useImperativeHandle(ref, () => ({
    submit: form.submit
  }))

  const fieldOptions = useMemo(() => {
    const fieldDefs = getFields(moduleName)

    return _([])
      .union(
        Object.values(fieldDefs.data),
        [{ label: 'Line Items', path: fieldDefs.lineItemPrefix }],
        Object.values(fieldDefs.lineItems).map(li => ({
          ...li,
          label: 'Line Item: ' + li.label,
          path: fieldDefs.lineItemPrefix + '[_].' + li.path
        }))
      )
      .map(opt => {
        return {
          label: opt.label,
          value: opt.path
        }
      })
      .value()
  }, [moduleName])

  const onRegionTypeChange = e => {
    setRegionType(e.target.value)
    if (e.target.value === RegionType.ANCHOR_POINT) {
      region.previousField = region.field
      region.value = region.previousValue
      region.field = ''
    } else {
      region.previousValue = region.value
      region.field = region.previousField
      region.value = ''
    }
    region.regionType = e.target.value
  }

  return (
    <Form
      form={form}
      initialValues={region}
      onFinish={values => {
        onSave({
          ...region,
          ...values
        })
      }}
    >
      <Form.Item
        id="regionTypeRadioGroup"
        label="Region Type"
        name="regionType"
      >
        <Radio.Group onChange={onRegionTypeChange}>
          <Radio id="regionTypeFieldRadioButton" value={RegionType.FIELD}>
            Field
          </Radio>
          <Radio
            id="regionTypeAnchorPointRadioButton"
            value={RegionType.ANCHOR_POINT}
          >
            Anchor point
          </Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item id="fieldSelect" label="Field" name="field">
        <Select
          showSearch
          optionFilterProp="label"
          options={fieldOptions}
          ref={firstFieldRef}
          disabled={regionType !== RegionType.FIELD}
        />
      </Form.Item>

      <Form.Item name="value">
        <Input
          id="anchorPointTextInput"
          placeholder="Text of anchor point"
          disabled={regionType !== RegionType.ANCHOR_POINT}
        />
      </Form.Item>
    </Form>
  )
})

FormContent.displayName = 'FormContent'

FormContent.propTypes = {
  moduleName: PropTypes.string.isRequired,
  region: PropTypes.shape({ field: PropTypes.string }).isRequired,
  onSave: PropTypes.func.isRequired
}
